import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Heart, HandHeart, Users, Calendar, CheckCircle } from 'lucide-react';
import { volunteerOpportunities } from '../data/mock';
import { toast } from 'sonner';

const GetInvolved = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    interests: [],
    availability: '',
    experience: '',
    motivation: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/volunteers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit volunteer application');
      }
      
      const data = await response.json();
      console.log('Volunteer application:', data);
      
      setIsSubmitted(true);
      toast.success('Thank you for your interest! We will contact you soon.');
      
      setTimeout(() => {
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          interests: [],
          availability: '',
          experience: '',
          motivation: ''
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting volunteer application:', error);
      toast.error('Failed to submit application. Please try again or contact us directly.');
    }
  };

  const iconMap = {
    UserCircle: Users,
    BookOpen: HandHeart,
    Calendar: Calendar,
    Briefcase: Users
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto border-0 shadow-2xl">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Thank You for Stepping Up!
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Your volunteer application has been received. Our volunteer coordinator will 
                reach out to you within 3-5 business days to discuss opportunities and next steps.
              </p>
              <div className="bg-orange-50 border-l-4 border-orange-600 p-4 text-left">
                <p className="text-sm text-gray-700">
                  <strong>What's next?</strong><br />
                  • Check your email for confirmation<br />
                  • We'll schedule an orientation call<br />
                  • Complete background check (if required)<br />
                  • Start making a difference!
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Heart className="w-16 h-16 mx-auto mb-6 text-white" fill="white" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Get Involved
            </h1>
            <p className="text-xl text-orange-100 leading-relaxed">
              Join our community of compassionate volunteers making a real difference 
              in the lives of veterans, seniors, youth, and individuals rebuilding their futures.
            </p>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-4xl font-bold text-orange-600 mb-2">150+</div>
                <div className="text-sm text-gray-600">Active Volunteers</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-orange-600 mb-2">5,000+</div>
                <div className="text-sm text-gray-600">Volunteer Hours</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-orange-600 mb-2">500+</div>
                <div className="text-sm text-gray-600">Lives Impacted</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-orange-600 mb-2">12+</div>
                <div className="text-sm text-gray-600">Programs</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Volunteer Opportunities */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Volunteer <span className="text-orange-600">Opportunities</span>
              </h2>
              <p className="text-xl text-gray-600">
                Find the perfect way to contribute your time and talents
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {volunteerOpportunities.map((opportunity, index) => {
                const IconComponent = iconMap[opportunity.icon];
                return (
                  <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {opportunity.title}
                          </h3>
                          <p className="text-gray-600 mb-3 leading-relaxed">
                            {opportunity.description}
                          </p>
                          <div className="inline-block px-3 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded-full">
                            {opportunity.commitment}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Volunteer Application Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Volunteer Application
              </h2>
              <p className="text-lg text-gray-600">
                Ready to make a difference? Fill out the form below to get started.
              </p>
            </div>

            <Card className="border-0 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50 border-b">
                <CardTitle className="text-2xl">Join Our Team</CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b">
                      Personal Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          placeholder="Enter your first name"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          placeholder="Enter your last name"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b">
                      Contact Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your.email@example.com"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="(555) 123-4567"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Interests */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b">
                      Areas of Interest
                    </h3>
                    <div className="space-y-3">
                      {volunteerOpportunities.map((opportunity, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <Checkbox
                            id={`interest-${index}`}
                            checked={formData.interests.includes(opportunity.title)}
                            onCheckedChange={() => handleInterestToggle(opportunity.title)}
                          />
                          <Label
                            htmlFor={`interest-${index}`}
                            className="font-normal cursor-pointer"
                          >
                            {opportunity.title}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Availability */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b">
                      Availability
                    </h3>
                    <div>
                      <Label htmlFor="availability">When are you available to volunteer? *</Label>
                      <Select
                        value={formData.availability}
                        onValueChange={(value) => setFormData({ ...formData, availability: value })}
                        required
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select your availability" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekday-mornings">Weekday Mornings</SelectItem>
                          <SelectItem value="weekday-afternoons">Weekday Afternoons</SelectItem>
                          <SelectItem value="weekday-evenings">Weekday Evenings</SelectItem>
                          <SelectItem value="weekends">Weekends</SelectItem>
                          <SelectItem value="flexible">Flexible Schedule</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Experience & Motivation */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b">
                      Tell Us About Yourself
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="experience">
                          Relevant Experience or Skills (Optional)
                        </Label>
                        <Textarea
                          id="experience"
                          name="experience"
                          value={formData.experience}
                          onChange={handleChange}
                          placeholder="Share any relevant experience, skills, or training..."
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="motivation">
                          Why do you want to volunteer with us? *
                        </Label>
                        <Textarea
                          id="motivation"
                          name="motivation"
                          value={formData.motivation}
                          onChange={handleChange}
                          required
                          placeholder="Tell us what motivates you to serve..."
                          rows={4}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Submit Application
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Additional Ways to Help */}
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              <Card className="border-2 border-orange-200 bg-orange-50">
                <CardContent className="p-6">
                  <HandHeart className="w-10 h-10 text-orange-600 mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">Make a Donation</h3>
                  <p className="text-sm text-gray-700 mb-4">
                    Can't volunteer? Your financial support helps us serve more people.
                  </p>
                  <a href="/donate">
                    <Button variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white">
                      Donate Now
                    </Button>
                  </a>
                </CardContent>
              </Card>

              <Card className="border-2 border-orange-200 bg-orange-50">
                <CardContent className="p-6">
                  <Users className="w-10 h-10 text-orange-600 mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">Corporate Partnerships</h3>
                  <p className="text-sm text-gray-700 mb-4">
                    Interested in team volunteering or corporate sponsorship?
                  </p>
                  <a href="/contact">
                    <Button variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white">
                      Contact Us
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetInvolved;
